import React, { useState, useCallback, useMemo, useEffect } from 'react';
import {
  Box,
  TextField,
  Button,
  Typography,
  Alert,
  Paper,
  Grid,
} from '@mui/material';
import katex from 'katex';
import 'katex/dist/katex.min.css';

import { CoordinateGrid } from '../coordinateGrid';
import {
  generateTrigGraphVariations,
  trigGraphElements,
  parsePiFraction,
  parseFraction,
  normalizeStep1Types,
  trigEquationToLatex,
  computeAsymptotesInOnePeriod,
  STEP1_TYPE_LABELS,
} from './trigGraphUtils';
import { parseInputWithLabel } from '../shared/inputLabels';

/**
 * Student component for answering trig graph questions
 *
 * Props:
 * - config: TrigGraphConfig
 * - value: StudentAnswer (current answer state)
 * - onChange: (answer) => void
 * - disabled: boolean (after submission)
 * - showCorrectness: boolean
 */
const TrigGraphStudent = ({
  config,
  value = {},
  onChange,
  disabled = false,
  showCorrectness = false,
}) => {
  const [step, setStep] = useState(value?.step2?.selectedGraphIndex !== undefined ? 2 : 1);
  const [amplitudeInput, setAmplitudeInput] = useState(value?.step1?.amplitude?.toString() || '');
  const [periodInput, setPeriodInput] = useState(value?.step1?.period?.toString() || '');
  const [phaseShiftInput, setPhaseShiftInput] = useState(value?.step1?.phaseShift?.toString() || '');
  const [verticalShiftInput, setVerticalShiftInput] = useState(value?.step1?.verticalShift?.toString() || '');
  const [yInterceptInput, setYInterceptInput] = useState(value?.step1?.yIntercept?.toString() || '');
  const [vaInputs, setVaInputs] = useState(value?.step1?.verticalAsymptotes?.map(String) || []);
  const [validationError, setValidationError] = useState('');
  const [selectedGraph, setSelectedGraph] = useState(value?.step2?.selectedGraphIndex ?? null);
  const [parseError, setParseError] = useState('');

  const { parsedTrig, grid, interceptTolerance = 0.05 } = config || {};

  const step1Types = normalizeStep1Types(config || {});
  const step1TypesKey = step1Types.join(',');
  const hasAmplitude = step1Types.includes('amplitude');
  const hasPeriod = step1Types.includes('period');
  const hasPhaseShift = step1Types.includes('phaseShift');
  const hasVerticalShift = step1Types.includes('verticalShift');
  const hasYIntercept = step1Types.includes('yIntercept');
  const hasVA = step1Types.includes('verticalAsymptotes');

  // Generate variations
  const variationsResult = useMemo(() => {
    if (!parsedTrig) return { variations: [], error: '' };
    try {
      const { variations } = generateTrigGraphVariations(
        parsedTrig, step1Types, config.variationSeed
      );
      return { variations, error: '' };
    } catch (e) {
      console.error('Failed to generate variations:', e);
      return { variations: [], error: 'Error loading question. Please contact your instructor.' };
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [parsedTrig, step1TypesKey, config?.variationSeed]);

  const variations = variationsResult.variations;

  useEffect(() => {
    setParseError(variationsResult.error);
  }, [variationsResult.error]);

  // Expected count for vertical asymptotes
  const expectedVACount = useMemo(() => {
    if (!parsedTrig) return 1;
    return computeAsymptotesInOnePeriod(parsedTrig).length || 1;
  }, [parsedTrig]);

  // Handle VA input changes
  const handleVAChange = useCallback((index, inputValue) => {
    const newVA = [...vaInputs];
    newVA[index] = inputValue;
    setVaInputs(newVA);
    setValidationError('');
  }, [vaInputs]);

  // Validate all step1 inputs and move to step 2
  const handleValidateStep1 = useCallback(() => {
    const errors = [];
    const step1Data = { timestamp: new Date().toISOString() };

    if (hasAmplitude) {
      const parsed = parseInputWithLabel(amplitudeInput);
      if (!parsed.valid) {
        errors.push('Please enter a valid amplitude value.');
      } else if (Math.abs(parsed.value - parsedTrig.amplitude) > interceptTolerance) {
        errors.push('Amplitude incorrect.');
      } else {
        step1Data.amplitude = parsed.value;
        step1Data.amplitudeLabel = parsed.display;
      }
    }

    if (hasPeriod) {
      const studentVal = parsePiFraction(periodInput);
      if (isNaN(studentVal)) {
        errors.push('Please enter a valid period value.');
      } else if (Math.abs(studentVal - parsedTrig.period) > interceptTolerance) {
        errors.push('Period incorrect.');
      } else {
        step1Data.period = periodInput; // Store raw string for server-side parsing
      }
    }

    if (hasPhaseShift) {
      const studentVal = parsePiFraction(phaseShiftInput);
      if (isNaN(studentVal)) {
        errors.push('Please enter a valid phase shift value.');
      } else if (Math.abs(studentVal - parsedTrig.phaseShift) > interceptTolerance) {
        errors.push('Phase shift incorrect.');
      } else {
        step1Data.phaseShift = phaseShiftInput;
      }
    }

    if (hasVerticalShift) {
      const parsed = parseInputWithLabel(verticalShiftInput);
      if (!parsed.valid) {
        errors.push('Please enter a valid vertical shift value.');
      } else if (Math.abs(parsed.value - parsedTrig.verticalShift) > interceptTolerance) {
        errors.push('Vertical shift incorrect.');
      } else {
        step1Data.verticalShift = parsed.value;
        step1Data.verticalShiftLabel = parsed.display;
      }
    }

    if (hasYIntercept) {
      const parsed = parseInputWithLabel(yInterceptInput);
      if (!parsed.valid) {
        errors.push('Please enter a valid y-intercept value.');
      } else if (parsedTrig.yIntercept === undefined || Math.abs(parsed.value - parsedTrig.yIntercept) > interceptTolerance) {
        errors.push('Y-intercept incorrect.');
      } else {
        step1Data.yIntercept = parsed.value;
        step1Data.yInterceptLabel = parsed.display;
      }
    }

    if (hasVA) {
      const correctVAs = computeAsymptotesInOnePeriod(parsedTrig).sort((a, b) => a - b);
      const studentVAValues = vaInputs.map(v => parsePiFraction(v)).filter(v => !isNaN(v));
      if (studentVAValues.length !== correctVAs.length) {
        errors.push(`Please enter ${correctVAs.length} vertical asymptote value(s).`);
      } else {
        const sortedStudent = [...studentVAValues].sort((a, b) => a - b);
        const vaCorrect = correctVAs.length === sortedStudent.length &&
          correctVAs.every((r, i) => Math.abs(r - sortedStudent[i]) <= interceptTolerance);
        if (!vaCorrect) {
          errors.push('Vertical asymptote(s) incorrect.');
        } else {
          step1Data.verticalAsymptotes = vaInputs; // Store raw strings
        }
      }
    }

    if (errors.length > 0) {
      setValidationError(errors.join(' ') + ' Please check your calculations.');
      return;
    }

    setStep(2);
    setValidationError('');
    onChange({ ...value, step1: step1Data });
  }, [amplitudeInput, periodInput, phaseShiftInput, verticalShiftInput,
      yInterceptInput, vaInputs, hasAmplitude, hasPeriod, hasPhaseShift,
      hasVerticalShift, hasYIntercept, hasVA, parsedTrig, interceptTolerance,
      value, onChange]);

  // Handle graph selection
  const handleGraphSelect = useCallback((index) => {
    if (disabled) return;

    setSelectedGraph(index);
    onChange({
      ...value,
      step1: value.step1 || {},
      step2: {
        selectedGraphIndex: index,
        timestamp: new Date().toISOString(),
      },
    });
  }, [disabled, value, onChange]);

  if (parseError) {
    return (
      <Box sx={{ p: 2 }}>
        <Alert severity="error">{parseError}</Alert>
      </Box>
    );
  }

  if (!config || !parsedTrig) {
    return (
      <Box sx={{ p: 2 }}>
        <Alert severity="warning">Question configuration is missing.</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      {/* Equation Display */}
      <Paper sx={{ p: 2 }}>
        <Typography variant="h6" align="center">
          <span dangerouslySetInnerHTML={{
            __html: katex.renderToString(trigEquationToLatex(config.equation), { throwOnError: false })
          }} />
        </Typography>
      </Paper>

      {/* Step 1: Value Input */}
      <Paper sx={{ p: 2 }}>
        <Typography variant="h6" gutterBottom>
          Step 1: Find the {step1Types.map(t => STEP1_TYPE_LABELS[t]?.toLowerCase() || t).join(' and ')}
        </Typography>

        {/* Amplitude */}
        {hasAmplitude && (
          <>
            {step1Types.length > 1 && (
              <Typography variant="subtitle2" sx={{ mb: 1 }}>Amplitude:</Typography>
            )}
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2 }}>
              <TextField
                label="Amplitude"
                value={amplitudeInput}
                onChange={(e) => { setAmplitudeInput(e.target.value); setValidationError(''); }}
                disabled={step === 2 || disabled}
                placeholder="e.g., 2, 1/2, 3"
                sx={{ width: 180 }}
              />
            </Box>
          </>
        )}

        {/* Period */}
        {hasPeriod && (
          <>
            {step1Types.length > 1 && (
              <Typography variant="subtitle2" sx={{ mb: 1 }}>Period:</Typography>
            )}
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2 }}>
              <TextField
                label="Period"
                value={periodInput}
                onChange={(e) => { setPeriodInput(e.target.value); setValidationError(''); }}
                disabled={step === 2 || disabled}
                placeholder="e.g., pi, 2pi/3, pi/2"
                sx={{ width: 180 }}
              />
            </Box>
          </>
        )}

        {/* Phase Shift */}
        {hasPhaseShift && (
          <>
            {step1Types.length > 1 && (
              <Typography variant="subtitle2" sx={{ mb: 1 }}>Phase Shift:</Typography>
            )}
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2 }}>
              <TextField
                label="Phase Shift"
                value={phaseShiftInput}
                onChange={(e) => { setPhaseShiftInput(e.target.value); setValidationError(''); }}
                disabled={step === 2 || disabled}
                placeholder="e.g., pi/6, -pi/4, 0"
                sx={{ width: 180 }}
              />
            </Box>
          </>
        )}

        {/* Vertical Shift */}
        {hasVerticalShift && (
          <>
            {step1Types.length > 1 && (
              <Typography variant="subtitle2" sx={{ mb: 1 }}>Vertical Shift:</Typography>
            )}
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2 }}>
              <TextField
                label="Vertical Shift"
                value={verticalShiftInput}
                onChange={(e) => { setVerticalShiftInput(e.target.value); setValidationError(''); }}
                disabled={step === 2 || disabled}
                placeholder="e.g., 0, 1, -2"
                sx={{ width: 180 }}
              />
            </Box>
          </>
        )}

        {/* Y-Intercept */}
        {hasYIntercept && (
          <>
            {step1Types.length > 1 && (
              <Typography variant="subtitle2" sx={{ mb: 1 }}>Y-Intercept:</Typography>
            )}
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2 }}>
              <TextField
                label="Y-Intercept"
                value={yInterceptInput}
                onChange={(e) => { setYInterceptInput(e.target.value); setValidationError(''); }}
                disabled={step === 2 || disabled}
                placeholder="e.g., 0, 1, -3/2"
                sx={{ width: 180 }}
              />
            </Box>
          </>
        )}

        {/* Vertical Asymptotes */}
        {hasVA && (
          <>
            {step1Types.length > 1 && (
              <Typography variant="subtitle2" sx={{ mb: 1 }}>Vertical Asymptotes:</Typography>
            )}
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2 }}>
              {Array.from({ length: expectedVACount }).map((_, index) => (
                <TextField
                  key={`va-${index}`}
                  label={`asymptote ${index + 1}`}
                  value={vaInputs[index] || ''}
                  onChange={(e) => handleVAChange(index, e.target.value)}
                  disabled={step === 2 || disabled}
                  placeholder="e.g., pi/2, -pi/4"
                  sx={{ width: 180 }}
                />
              ))}
            </Box>
          </>
        )}

        {validationError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {validationError}
          </Alert>
        )}

        {step === 1 && !disabled && (
          <Button
            variant="contained"
            onClick={handleValidateStep1}
            disabled={(() => {
              if (hasAmplitude && !amplitudeInput?.trim?.()) return true;
              if (hasPeriod && !periodInput?.trim?.()) return true;
              if (hasPhaseShift && !phaseShiftInput?.trim?.()) return true;
              if (hasVerticalShift && !verticalShiftInput?.trim?.()) return true;
              if (hasYIntercept && !yInterceptInput?.trim?.()) return true;
              if (hasVA && vaInputs.filter(v => v?.trim?.()).length < expectedVACount) return true;
              return false;
            })()}
          >
            Check & Continue
          </Button>
        )}

        {step === 2 && (
          <Alert severity="success">
            Correct! Now select the graph that matches this trig function.
          </Alert>
        )}
      </Paper>

      {/* Step 2: Graph Selection */}
      {step === 2 && (
        <Paper sx={{ p: 2 }}>
          <Typography variant="h6" gutterBottom>
            Step 2: Select the correct graph
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            All graphs share the value(s) you found.
            Choose the one that correctly represents the given trig function.
          </Typography>

          <Grid container spacing={2}>
            {variations.map((variation, index) => {
              const isSelected = selectedGraph === index;
              const showResult = showCorrectness && isSelected;
              const isCorrectChoice = showResult && variation.isCorrect;
              const isWrongChoice = showResult && !variation.isCorrect;
              const showCorrectHint = showCorrectness && selectedGraph !== null && !isSelected && variation.isCorrect;

              let borderStyle = '1px solid grey';
              if (isCorrectChoice || showCorrectHint) borderStyle = '3px solid green';
              else if (isWrongChoice) borderStyle = '3px solid red';
              else if (isSelected) borderStyle = '3px solid blue';

              let bgColor = 'background.paper';
              if (isCorrectChoice || showCorrectHint) bgColor = 'success.light';
              else if (isWrongChoice) bgColor = 'error.light';

              return (
                <Grid item xs={6} key={variation.label}>
                  <Paper
                    onClick={() => handleGraphSelect(index)}
                    sx={{
                      p: 1,
                      cursor: disabled ? 'default' : 'pointer',
                      border: borderStyle,
                      bgcolor: bgColor,
                      '&:hover': disabled ? {} : {
                        borderColor: 'primary.main',
                        bgcolor: showResult ? undefined : 'action.hover',
                      },
                    }}
                  >
                    <Typography variant="subtitle2" align="center">
                      Graph {variation.label}
                      {isCorrectChoice && ' (Correct!)'}
                      {isWrongChoice && ' (Incorrect)'}
                      {showCorrectHint && ' (Correct)'}
                    </Typography>
                    <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                      <CoordinateGrid
                        config={grid}
                        elements={trigGraphElements(variation.trig, grid)}
                        pendingPoints={[]}
                        mode="point"
                        readOnly={true}
                        width={180}
                        height={180}
                      />
                    </Box>
                  </Paper>
                </Grid>
              );
            })}
          </Grid>
        </Paper>
      )}
    </Box>
  );
};

export default TrigGraphStudent;
